import {
  isNotFoundLocation,
  type EmptyParams,
  type FlatIntersection,
  type GenericLocation,
  type LocationIn,
  type MatchedLocation,
  type MatchedLocationIn,
  type NotFoundLocation,
  type PathWithParam,
  type PopPath,
  type UnmatchedLocation,
} from './location';
import { logError, logWarn } from '@ardoq/logging';
import {
  readParam,
  writeParam,
  type ParamDeclarations,
  type Params,
} from './paramRegistry';
import {
  isIndexNode,
  isLayoutNode,
  isParamNode,
  type IndexNode,
  type IndexRouteContent,
  type PathNode,
  type RouteNode,
} from './routeNode';

const stripTrailingSlash = (path: string) =>
  path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

const splitPath = (path: string) => {
  const nextSlash = path.indexOf('/', 1);
  const segment = nextSlash === -1 ? path : path.slice(0, nextSlash);
  const remainingPath = nextSlash === -1 ? null : path.slice(nextSlash);

  return [segment, remainingPath];
};

const extractParam = (node: RouteNode, segment: string): Partial<Params> => {
  if (!isParamNode(node)) {
    return {};
  }

  const paramName = node.path.slice(2) as keyof Params;
  return {
    [paramName]: readParam(paramName, segment),
  };
};

type MatchState = {
  route: string;
  activeRoutes: RouteNode[];
  unmatchedSuffix: string | null;
};

/**
 * Traverse the route tree and find the active routes for the given path.
 */
const matchRoutesRecur = (
  state: MatchState,
  routeNodes: readonly RouteNode[] | undefined
): MatchState => {
  const [segment, remainingPath] = splitPath(state.unmatchedSuffix ?? '');

  let indexNode: IndexNode | undefined;
  for (const node of routeNodes ?? []) {
    if (isParamNode(node) && segment) {
      return matchRoutesRecur(
        {
          route: state.route + node.path,
          activeRoutes: [...state.activeRoutes, node],
          unmatchedSuffix: remainingPath,
        },
        node.children
      );
    } else if (node.path === segment) {
      return matchRoutesRecur(
        {
          route: state.route + node.path,
          activeRoutes: [...state.activeRoutes, node],
          unmatchedSuffix: remainingPath,
        },
        node.children
      );
    } else if (isLayoutNode(node)) {
      const layoutMatch = matchRoutesRecur(
        {
          ...state,
          activeRoutes: [...state.activeRoutes, node],
        },
        node.children
      );
      // The layout is considered a match if its recursion found anything
      // beyond the layout node itself — either by consuming URL via a child
      // path match or by matching an index route at the layout's children.
      // Without the activeRoutes check, a layout wrapping only an index
      // (e.g. `{ layout: true, children: [{ index: true }, '/invite'] }`)
      // would be silently skipped at the base path because no URL was consumed.
      if (layoutMatch.activeRoutes.length > state.activeRoutes.length + 1) {
        return layoutMatch;
      }
    } else if (isIndexNode(node)) {
      indexNode = node;
    }
  }

  if (!segment && indexNode) {
    return {
      ...state,
      activeRoutes: [...state.activeRoutes, indexNode],
    };
  }

  return state;
};

const hasUnmatchedSuffix = (
  state: MatchState
): state is MatchState & { unmatchedSuffix: string } =>
  Boolean(state.unmatchedSuffix);

const isPartialMatch = (
  state: MatchState
): state is MatchState & { unmatchedSuffix: string } =>
  hasUnmatchedSuffix(state) && state.activeRoutes.length > 0;

const matchRoutes = (path: string, routeTree: RouteNode) =>
  matchRoutesRecur(
    {
      route: '',
      activeRoutes: [],
      unmatchedSuffix: path,
    },
    [routeTree]
  );

const isPathRoute = (route: RouteNode): route is PathNode =>
  typeof route.path === 'string';

const readParams = (routes: RouteNode[], path: string): Partial<Params> => {
  const { params } = routes.filter(isPathRoute).reduce<{
    params: Partial<Params>;
    remainingPath: string;
  }>(
    ({ params, remainingPath }, route) => {
      const [segment, rest] = splitPath(remainingPath);
      return {
        params:
          segment && isParamNode(route)
            ? { ...params, ...extractParam(route, segment.slice(1)) }
            : params,
        remainingPath: rest ?? '',
      };
    },
    { params: {}, remainingPath: path }
  );

  return params;
};

const warnedUnregisteredParams = new Set<string>();

const warnUnregisteredParamOnce = (key: string): void => {
  if (warnedUnregisteredParams.has(key)) {
    return;
  }
  warnedUnregisteredParams.add(key);
  logWarn(Error('Unregistered search parameter in URL'), null, {
    key,
    fingerprint: ['unregistered-search-param', key],
  });
};

const warnedUnmatchedRoutes = new Set<string>();

const warnUnmatchedSuffixOnce = (
  route: string,
  unmatchedSuffix: string
): void => {
  if (warnedUnmatchedRoutes.has(route)) {
    return;
  }
  warnedUnmatchedRoutes.add(route);
  logWarn(Error('Unmatched path segment in URL'), null, {
    route,
    unmatchedSuffix,
    fingerprint: ['unmatched-path-segment', route],
  });
};

const loggedUnresolvedRedirects = new Set<string>();

const logUnresolvedRedirectErrorOnce = (
  route: string,
  target: string
): void => {
  if (loggedUnresolvedRedirects.has(route)) {
    return;
  }
  loggedUnresolvedRedirects.add(route);
  logError(Error('Redirect target did not resolve'), null, {
    route,
    target,
    fingerprint: ['unresolved-route-redirect', route],
  });
};

const searchParamsDeclaredOn = (node: RouteNode): ParamDeclarations => {
  const declared = 'searchParams' in node ? node.searchParams : undefined;
  return declared ?? {};
};

const collectSearchParamDeclarations = (
  node: RouteNode,
  parentRoute: string,
  inherited: ParamDeclarations,
  collected: Map<string, ParamDeclarations>
): void => {
  const route = isPathRoute(node) ? parentRoute + node.path : parentRoute;
  const declarations = { ...inherited, ...searchParamsDeclaredOn(node) };

  collected.set(route, { ...collected.get(route), ...declarations });

  for (const child of node.children ?? []) {
    collectSearchParamDeclarations(child, route, declarations, collected);
  }
};

const searchParamDeclarationsByTree = new WeakMap<
  RouteNode,
  ReadonlyMap<string, ParamDeclarations>
>();

const searchParamDeclarationsInTree = (
  routeTree: RouteNode
): ReadonlyMap<string, ParamDeclarations> => {
  const cached = searchParamDeclarationsByTree.get(routeTree);
  if (cached) {
    return cached;
  }

  const collected = new Map<string, ParamDeclarations>();
  collectSearchParamDeclarations(routeTree, '', {}, collected);
  searchParamDeclarationsByTree.set(routeTree, collected);
  return collected;
};

/**
 * The search parameters declared for a route: its own, and its ancestors'.
 */
export const searchParamsDeclaredAt = (
  routeTree: RouteNode,
  route: string
): ParamDeclarations =>
  searchParamDeclarationsInTree(routeTree).get(route) ?? {};

/**
 * The search parameters declared for the route a location stands at, including
 * the prefix route of a location that was not found.
 */
type RouteSearchParamLocation =
  | Pick<NotFoundLocation, 'kind' | 'matchedRoute' | 'route' | 'searchParams'>
  | {
      readonly kind?: 'matched' | 'unmatched';
      readonly route: string;
      readonly searchParams?: Readonly<Record<string, unknown>>;
    };

export const searchParamsDeclaredFor = (
  routeTree: RouteNode,
  location: RouteSearchParamLocation
): ParamDeclarations =>
  searchParamsDeclaredAt(
    routeTree,
    location.kind === 'not-found' ? location.matchedRoute : location.route
  );

const searchParamHandler = (
  paramName: string,
  declarations: ParamDeclarations
): ParamDeclarations[string] | undefined =>
  Object.hasOwn(declarations, paramName) ? declarations[paramName] : undefined;

type RedirectState = {
  routeMatches: MatchState;
  params: Partial<Params>;
  searchParams: Readonly<Record<string, unknown>>;
};

/**
 * Carry the search params of the matched location over to the route it
 * redirects to, dropping the ones the target parses differently or not at all.
 */
const searchParamsAfterRedirect = (
  searchParams: Readonly<Record<string, unknown>>,
  sourceDeclarations: ParamDeclarations,
  targetDeclarations: ParamDeclarations
): Readonly<Record<string, unknown>> =>
  Object.fromEntries(
    Object.entries(searchParams).filter(([paramName]) => {
      const handler = searchParamHandler(paramName, sourceDeclarations);
      return (
        handler !== undefined &&
        handler === searchParamHandler(paramName, targetDeclarations)
      );
    })
  );

/**
 * Resolve the redirect of the deepest active route, and of the route it lands
 * on, until one does not redirect.
 *
 * Only the deepest active route is consulted: a redirect says this exact route
 * moved, so walking upwards would silently give subtree behaviour.
 */
const followRedirects = (
  state: RedirectState,
  routeTree: RouteNode
): RedirectState => {
  const visitedRoutes = new Set<string>();
  let currentState = state;

  while (!visitedRoutes.has(currentState.routeMatches.route)) {
    visitedRoutes.add(currentState.routeMatches.route);
    const redirect = currentState.routeMatches.activeRoutes.at(-1)?.redirect;
    if (!redirect) {
      return currentState;
    }

    const target = matchRoutes(redirect.route, routeTree);
    if (target.route !== redirect.route || target.unmatchedSuffix !== null) {
      logUnresolvedRedirectErrorOnce(
        currentState.routeMatches.route,
        redirect.route
      );
      return currentState;
    }

    currentState = {
      routeMatches: target,
      params: { ...currentState.params, ...redirect.params },
      searchParams: {
        ...searchParamsAfterRedirect(
          currentState.searchParams,
          searchParamsDeclaredAt(routeTree, currentState.routeMatches.route),
          searchParamsDeclaredAt(routeTree, target.route)
        ),
        ...redirect.searchParams,
      },
    };
  }

  return currentState;
};

export const readRouteSearchParams = (
  urlSearchParams: URLSearchParams | undefined,
  declarations: ParamDeclarations
): Readonly<Record<string, unknown>> => {
  if (!urlSearchParams) return {};

  const result: Record<string, unknown> = {};
  for (const [key, value] of urlSearchParams.entries()) {
    const handler = searchParamHandler(key, declarations);
    if (!handler) {
      warnUnregisteredParamOnce(key);
      continue;
    }
    result[key] = handler.fromString(value);
  }

  return result;
};

export const matchLocation = <RouteTree extends RouteNode>(
  location: LocationIn<RouteTree>,
  routeTree: RouteTree,
  action: 'push' | 'replace' = 'push'
): MatchedLocationIn<RouteTree> | UnmatchedLocation => {
  const { routeMatches, params, searchParams } = followRedirects(
    {
      routeMatches: matchRoutes(location.route, routeTree),
      params: location.params,
      searchParams: location.searchParams ?? {},
    },
    routeTree
  );

  return {
    ...routeMatches,
    kind: routeMatches.route === '' ? 'unmatched' : 'matched',
    params,
    searchParams,
    hiddenParams: location.hiddenParams ?? {},
    hash: location.hash,
    action,
    // Need to cast this, because `matchRoutes` doesn't parse params, so there is no correspondence between the route
    // and the params.
  } as MatchedLocationIn<RouteTree> | UnmatchedLocation;
};

/**
 * Match a path against the route tree.
 *
 * Defaults to replacing the current history entry, for the load, reload and
 * back/forward cases where the browser is already at the path and pushing
 * would duplicate the entry it is standing on. Pass `'push'` for a path the
 * user is navigating to.
 */
export const matchPath = <RouteTree extends RouteNode>(
  path: string,
  urlSearchParams: URLSearchParams | undefined,
  routeTree: RouteTree,
  hiddenParams?: Partial<Params>,
  hash?: string,
  action: 'push' | 'replace' = 'replace'
): MatchedLocationIn<RouteTree> | UnmatchedLocation | NotFoundLocation => {
  const normalizedPath = stripTrailingSlash(path);
  const matched = matchRoutes(normalizedPath, routeTree);
  const matchedSearchParams = readRouteSearchParams(
    urlSearchParams,
    searchParamsDeclaredAt(routeTree, matched.route)
  );
  const matchedParams = readParams(matched.activeRoutes, normalizedPath);
  const isTotalMissWithNotFound =
    hasUnmatchedSuffix(matched) &&
    matched.activeRoutes.length === 0 &&
    Boolean(resolveNotFoundContent(matched.activeRoutes, routeTree));

  if (isPartialMatch(matched) || isTotalMissWithNotFound) {
    if (matched.activeRoutes.length > 0) {
      warnUnmatchedSuffixOnce(matched.route, matched.unmatchedSuffix);
    }

    return {
      kind: 'not-found',
      route: '',
      matchedRoute: matched.route,
      params: matchedParams,
      activeRoutes: matched.activeRoutes,
      unmatchedSuffix: matched.unmatchedSuffix,
      requestedPath: normalizedPath,
      searchParams: matchedSearchParams,
      hiddenParams: hiddenParams ?? {},
      hash,
      action,
    };
  }

  const { routeMatches, params, searchParams } = followRedirects(
    {
      routeMatches: matched,
      params: matchedParams,
      searchParams: matchedSearchParams,
    },
    routeTree
  );

  return {
    ...routeMatches,
    kind: routeMatches.route === '' ? 'unmatched' : 'matched',
    params,
    searchParams,
    hiddenParams: hiddenParams ?? {},
    hash,
    action,
  } as MatchedLocationIn<RouteTree> | UnmatchedLocation;
};

export const interpolatePath = <
  Path extends string,
  RouteParams extends EmptyParams,
>(
  location: GenericLocation<Path, RouteParams>
): string => {
  let path: string = location.route;
  for (const entry of Object.entries(location.params)) {
    const key = entry[0] as keyof Params;
    if (!path.includes(`/:${key}`)) {
      continue;
    }

    const value = entry[1] as Params[keyof Params];
    path = path.replace(`/:${key}`, `/${writeParam(key, value)}`);
  }
  return path;
};

/**
 * A not found location has no route to interpolate, so it writes back the path
 * that was asked for rather than the prefix that matched.
 */
export const locationToPathname = (
  location: MatchedLocation | NotFoundLocation
): string =>
  isNotFoundLocation(location)
    ? location.requestedPath
    : interpolatePath(location);

export const toRouteSearchParams = (
  params: Readonly<Record<string, unknown>> | undefined,
  declarations: ParamDeclarations
): URLSearchParams => {
  const searchParams = new URLSearchParams();
  if (!params) {
    return searchParams;
  }

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) {
      continue;
    }

    const handler = searchParamHandler(key, declarations);
    if (!handler) {
      warnUnregisteredParamOnce(key);
      continue;
    }
    searchParams.set(key, handler.toString(value as never));
  }
  return searchParams;
};

export const locationSearchParams = (
  routeTree: RouteNode,
  location: RouteSearchParamLocation
): URLSearchParams =>
  toRouteSearchParams(
    location.searchParams,
    searchParamsDeclaredFor(routeTree, location)
  );

export const popRoute = <Route extends string>(route: Route) =>
  route.split('/').slice(0, -1).join('/') as PopPath<Route>;

export const popLocation = <Loc extends GenericLocation>(location: Loc) => {
  const parentRoute = popRoute(location.route);

  return {
    ...location,
    route: parentRoute,
  };
};

export const doesLocationHaveParam = <
  Path extends string,
  LocParams extends EmptyParams,
  ParamName extends string,
>(
  location: GenericLocation<Path, LocParams>,
  paramName: ParamName
): location is FlatIntersection<
  GenericLocation<Path, LocParams>,
  GenericLocation<
    PathWithParam<Path, ParamName>,
    LocParams & Pick<Params, keyof Params & ParamName>
  >
> => paramName in location.params;

export type NotFoundContent = {
  /**
   * The active routes up to and including the one declaring the not found
   * content. Their layouts stay mounted around it.
   */
  prefix: RouteNode[];
  notFound: () => IndexRouteContent;
};

/**
 * Find the not found content for a chain of active routes, by walking from the
 * deepest match upwards to the first route that declares one.
 *
 * Returns `null` when no route in the chain declares not found content.
 */
export const resolveNotFoundContent = (
  activeRoutes: RouteNode[],
  routeTree: RouteNode
): NotFoundContent | null => {
  // `matchRoutes` drops a layout node when nothing below it matched, so a total
  // miss against a layout root comes back with no active routes at all. The
  // root is active regardless, so seed the chain with it.
  const chain = activeRoutes.length > 0 ? activeRoutes : [routeTree];
  const depth = chain.findLastIndex(route => route.notFound);
  const owner = chain[depth];
  if (!owner?.notFound) {
    return null;
  }

  return {
    prefix: chain.slice(0, depth + 1),
    notFound: owner.notFound,
  };
};

export const pathBelongsToRouteTree = (
  path: string,
  routeTree: RouteNode
): boolean => {
  const matched = matchRoutes(stripTrailingSlash(path), routeTree);
  const deepestActiveRoute = matched.activeRoutes.at(-1);
  if (
    matched.unmatchedSuffix &&
    deepestActiveRoute?.redirect &&
    !deepestActiveRoute.children?.length
  ) {
    return false;
  }
  if (matched.activeRoutes.some(isPathRoute)) return true;
  // An index route consumes no path segment, so a match ending at one — like
  // the root redirect to home — has no path route to find above. It still
  // belongs when the whole path matched into something beyond the root.
  return !matched.unmatchedSuffix && matched.activeRoutes.length > 1;
};
